import { DataTable } from "@/components/data-table";
import { ConfirmDialog, useOpenModal } from "@/components/modal";
import { getMontoMonedaCantidad, getTotalMonedaTransferencia, getTransferenciaMetodoMonedaCantidad } from "@/features/monetizacion/shared/utils";
import { formatDate, formatNumber, formatString } from "@/shared/formats";
import { CajaApertura } from "@/shared/types/caja-apertura";
import { TransferenciaItemRequest, TransferenciaRequest } from "@/shared/types/transferencia";
import { Stack, StackProps, Typography } from "@mui/material";
import { z } from "zod/v4";
import { useCajaContext } from "../contexts/caja-context";
import { BodyTypography, HeaderTypography } from "../../shared/components/table-typography";
import { Form, FormProps, useFormContext } from "@/components/form";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { useMemo } from "react";

export const closeCajaFormSchema = z.object({
    transferenciaReal: z.object({
        items: z.array(z.object({
            monto: z.object({
                items: z.array(z.object({
                    monedaId: z.string(),
                    cantidad: z.number({ message: "" })
                }))
            }),
            transferenciaMetodoId: z.string().nonempty("Este campo es requerido")
        })).transform(v => v.filter(vi => vi.monto).map(vi => vi as TransferenciaItemRequest))
    })
})

interface CloseCajaFormProps extends FormProps<z.input<typeof closeCajaFormSchema>> {
    cajaApertura: CajaApertura
}

export default function CloseCajaForm({
    cajaApertura,
    form,
    ...props
}: CloseCajaFormProps) {
    const openModal = useOpenModal();
    const { cajaData } = useCajaContext();

    return <Form form={form} gap={3} {...props}>
        <Stack direction={"row"} gap={4}>
            <Typography variant="subtitle1">Fecha de Apertura: </Typography>
            <Typography variant="body1">{formatDate(cajaApertura.fechaApertura, true)}</Typography>
        </Stack>
        <Stack gap={1}>
            <form.Field name="transferenciaReal.items" mode="array">
                {field => field.state.value.map((ti, index) => <TransferenciaMetodoSummaryTable
                    key={ti.transferenciaMetodoId}
                    cajaApertura={cajaApertura}
                    index={index}
                    item={ti}
                />)}
            </form.Field>
        </Stack>
        <Stack gap={1} direction={"row"}>
            <CloseCajaTotalTable cajaApertura={cajaApertura} />
        </Stack>
        <Form.SubmitButton onClick={e => {
            e.preventDefault();
            openModal("confirmClose");
        }}>Cerrar Caja</Form.SubmitButton>

        <ConfirmDialog code="confirmClose">
            <ConfirmDialog.Content
                title="Cerrar Caja"
                description={`¿Estás seguro que quieres cerrar la caja "${cajaData.nombre}"?`}
                onConfirm={async () => await form.handleSubmit()}
            />
        </ConfirmDialog>
    </Form>
}

interface TransferenciaMetodoSummaryTableProps extends StackProps {
    index: number,
    item: TransferenciaItemRequest,
    cajaApertura: CajaApertura,
}

function TransferenciaMetodoSummaryTable({
    index,
    item,
    cajaApertura,
    ...props
}: TransferenciaMetodoSummaryTableProps) {
    const { transferenciaMetodos, monedas: allMonedas } = useMonetizacion();

    const [transferenciaMetodo, monedas] = useMemo(() => [
        transferenciaMetodos.find(tm => tm.id === item.transferenciaMetodoId)!,
        allMonedas.filter(m => item.monto.items.find(mi => mi.monedaId === m.id))
    ], [cajaApertura])

    return <DataTable {...props}>
        <DataTable.Content
            rows={monedas}
            columns={[
                {
                    header: <HeaderTypography transferenciaMetodo={transferenciaMetodo} variant="h6">{transferenciaMetodo.nombre}</HeaderTypography>,
                    render: moneda => <BodyTypography moneda={moneda} transferenciaMetodo={transferenciaMetodo}>
                        {`${formatString(moneda.nombre)} (${formatString(moneda.simbolo)})`}
                    </BodyTypography>
                },
                {
                    header: <HeaderTypography transferenciaMetodo={transferenciaMetodo}>Monto Inicial</HeaderTypography>,
                    render: moneda => <BodyTypography moneda={moneda} transferenciaMetodo={transferenciaMetodo}>
                        {formatNumber(getTransferenciaMetodoMonedaCantidad(cajaApertura.transferenciaInicial, transferenciaMetodo.id, moneda.id))}
                    </BodyTypography>,
                    align: "right",
                    width: "150px",
                },
                {
                    header: <HeaderTypography transferenciaMetodo={transferenciaMetodo}>Monto Calculado</HeaderTypography>,
                    render: moneda => <BodyTypography moneda={moneda} transferenciaMetodo={transferenciaMetodo}>
                        {formatNumber(getTransferenciaMetodoMonedaCantidad(cajaApertura.transferenciaTotal, transferenciaMetodo.id, moneda.id))}
                    </BodyTypography>,
                    align: "right",
                    width: "150px",
                },
                {
                    header: <HeaderTypography transferenciaMetodo={transferenciaMetodo}>Monto en Caja</HeaderTypography>,
                    render: moneda => <Stack>
                        <Form.MonedaField
                            monedaId={moneda.id}
                            name={`transferenciaReal.items[${index}].monto.items[${item.monto.items.findIndex(ti => ti.monedaId === moneda.id)}].cantidad`}
                            size="small"
                        />
                    </Stack>,
                    align: "right",
                    width: "200px"
                },
                {
                    header: <HeaderTypography transferenciaMetodo={transferenciaMetodo}>Diferencia</HeaderTypography>,
                    render: moneda => <BodyTypography moneda={moneda} transferenciaMetodo={transferenciaMetodo}>
                        {formatNumber(getMontoMonedaCantidad(item.monto, moneda.id) - getTransferenciaMetodoMonedaCantidad(cajaApertura.transferenciaTotal, transferenciaMetodo.id, moneda.id))}
                    </BodyTypography>,
                    width: "150px",
                    align: "right"
                }
            ]}
        />
    </DataTable>
}

interface CloseCajaTotalTableProps extends StackProps {
    cajaApertura: CajaApertura,
}

function CloseCajaTotalTable({
    cajaApertura,
    ...props
}: CloseCajaTotalTableProps) {
    const form = useFormContext();

    const { monedas: allMonedas } = useMonetizacion();

    const monedas = useMemo(() => allMonedas.filter(m => {
        function isMonedaInTransferencia(transferencia: TransferenciaRequest, monedaId: string) {
            return Boolean(
                transferencia.items.find(ti => Boolean(
                    ti.monto.items.find(mi => mi.monedaId === monedaId)
                ))
            );
        }

        if (isMonedaInTransferencia(cajaApertura.transferenciaInicial, m.id)) return true;
        if (isMonedaInTransferencia(cajaApertura.transferenciaTotal, m.id)) return true;
        return false;
    }), [cajaApertura]);

    return <DataTable {...props}>
        <DataTable.Content
            rows={monedas}
            columns={[
                {
                    header: <HeaderTypography variant="h6">Totales</HeaderTypography>,
                    render: moneda => <BodyTypography moneda={moneda}>{`${formatString(moneda.nombre)} (${formatString(moneda.simbolo)})`}</BodyTypography>
                },
                {
                    header: <HeaderTypography>Inicial</HeaderTypography>,
                    render: moneda => <BodyTypography moneda={moneda}>
                        {formatNumber(getTotalMonedaTransferencia(cajaApertura.transferenciaInicial, moneda.id))}
                    </BodyTypography>,
                    align: "right"
                },
                {
                    header: <HeaderTypography>Calculado</HeaderTypography>,
                    render: moneda => <BodyTypography moneda={moneda}>
                        {formatNumber(getTotalMonedaTransferencia(cajaApertura.transferenciaTotal, moneda.id))}
                    </BodyTypography>,
                    align: "right"
                },
                {
                    header: <HeaderTypography>Real</HeaderTypography>,
                    render: moneda => <form.Subscribe selector={state => state.values.transferenciaReal as TransferenciaRequest}>
                        {transferenciaReal => <BodyTypography moneda={moneda}>
                            {formatNumber(getTotalMonedaTransferencia(transferenciaReal, moneda.id))}
                        </BodyTypography>}
                    </form.Subscribe>,
                    align: "right"
                },
                {
                    header: <HeaderTypography>Diferencia</HeaderTypography>,
                    render: moneda => <form.Subscribe selector={state => state.values.transferenciaReal as TransferenciaRequest}>
                        {transferenciaReal => {
                            const cantidadReal = getTotalMonedaTransferencia(transferenciaReal, moneda.id);
                            const cantidadTotal = getTotalMonedaTransferencia(cajaApertura.transferenciaTotal, moneda.id)
                            return <BodyTypography moneda={moneda}>
                                {formatNumber(cantidadReal - cantidadTotal)}
                            </BodyTypography>
                        }}
                    </form.Subscribe>,
                    align: "right"
                }
            ]}
        />
    </DataTable>
}